import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { JsonFileUpload } from "./JsonFileUpload";
import { CsvFileUpload } from "./CsvFileUpload";
import ContractUpload from "./ContractUpload";
import { BasicInfoTab } from "./tabs/BasicInfoTab";
import { PartyInfoTab } from "./tabs/PartyInfoTab";
import { FinancialTab } from "./tabs/FinancialTab";
import { PropertyTab } from "./tabs/PropertyTab";
import { NotesTab } from "./tabs/NotesTab";
import { BuyerInterviewTab } from "./tabs/BuyerInterviewTab";

interface Deal {
  id?: string;
  deal_title: string;
  seller_name: string;
  seller_email?: string;
  seller_phone?: string;
  seller_address?: string;
  buyer_name: string;
  buyer_address?: string;
  buyer_current_on_contract: boolean;
  buyer_payment_history?: string;
  date_of_sale?: string;
  first_payment_date?: string;
  first_payment_to_me?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  has_balloon_payment: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  property_type?: string;
  property_description?: string;
  property_address?: string;
  estimated_property_value?: number;
  current_balance?: number;
  payments_remaining?: number;
  status: string;
  bank_notes?: string;
  internal_notes?: string;
  offer_price?: number;
  offer_date?: string;
  investor_quote?: number;
  zillow_link?: string;
  // Buyer Interview fields
  buyer_current_balance_stated?: number;
  buyer_last_payment_date?: string;
  buyer_last_payment_amount?: number;
  buyer_late_payment_count?: number;
  buyer_late_payment_details?: string;
  buyer_past_due_amount?: number;
  buyer_occupies_property?: boolean;
  buyer_tenant_info?: string;
  buyer_major_repairs_needed?: string;
  buyer_improvements_made?: string;
  buyer_tax_payment_current?: boolean;
  buyer_tax_delinquent?: boolean;
  buyer_has_insurance?: boolean;
  buyer_insurance_company?: string;
  buyer_insurance_policy_number?: string;
  buyer_insurance_loss_payee?: string;
  buyer_hoa_current?: boolean;
  buyer_hoa_amount?: number;
  buyer_contract_default_status?: string;
  buyer_aware_of_assignment?: boolean;
  buyer_terms_confirmed?: boolean;
  buyer_agrees_new_payment_direction?: boolean;
  buyer_estoppel_signed?: boolean;
  buyer_estoppel_signed_date?: string;
  buyer_interview_completed_date?: string;
  buyer_interview_notes?: string;
}

interface CompactDealFormProps {
  deal?: Deal | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function CompactDealForm({ deal, onClose, onSuccess }: CompactDealFormProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState<Deal>({
    deal_title: deal?.deal_title || '',
    seller_name: deal?.seller_name || '',
    seller_email: deal?.seller_email || '',
    seller_phone: deal?.seller_phone || '',
    seller_address: deal?.seller_address || '',
    buyer_name: deal?.buyer_name || '',
    buyer_address: deal?.buyer_address || '',
    buyer_current_on_contract: deal?.buyer_current_on_contract ?? true,
    buyer_payment_history: deal?.buyer_payment_history || '',
    date_of_sale: deal?.date_of_sale || '',
    first_payment_date: deal?.first_payment_date || '',
    first_payment_to_me: deal?.first_payment_to_me || '',
    sales_price: deal?.sales_price || undefined,
    down_payment_amount: deal?.down_payment_amount || undefined,
    beginning_balance: deal?.beginning_balance || undefined,
    interest_rate: deal?.interest_rate || undefined,
    payment_amount: deal?.payment_amount || undefined,
    original_term_months: deal?.original_term_months || undefined,
    has_balloon_payment: deal?.has_balloon_payment || false,
    balloon_amount: deal?.balloon_amount || undefined,
    balloon_due_date: deal?.balloon_due_date || '',
    property_type: deal?.property_type || '',
    property_description: deal?.property_description || '',
    property_address: deal?.property_address || '',
    estimated_property_value: deal?.estimated_property_value || undefined,
    current_balance: deal?.current_balance || undefined,
    payments_remaining: deal?.payments_remaining || undefined,
    status: deal?.status || 'draft',
    bank_notes: deal?.bank_notes || '',
    internal_notes: deal?.internal_notes || '',
    offer_price: deal?.offer_price || undefined,
    offer_date: deal?.offer_date || '',
    investor_quote: deal?.investor_quote || undefined,
    zillow_link: deal?.zillow_link || ''
  });

  const handleInputChange = (field: keyof Deal, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExtractedData = (data: any) => {
    // Remove payments_made as it's calculated, not stored
    const { payments_made, ...restData } = data;
    setFormData(prev => ({
      ...prev,
      ...restData
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const dealData = {
        ...formData,
        sales_price: formData.sales_price || null,
        down_payment_amount: formData.down_payment_amount || null,
        beginning_balance: formData.beginning_balance || null,
        interest_rate: formData.interest_rate || null,
        payment_amount: formData.payment_amount || null,
        original_term_months: formData.original_term_months || null,
        balloon_amount: formData.balloon_amount || null,
        estimated_property_value: formData.estimated_property_value || null,
        current_balance: formData.current_balance || null,
        payments_remaining: formData.payments_remaining || null,
        offer_price: formData.offer_price || null,
        investor_quote: formData.investor_quote || null,
        date_of_sale: formData.date_of_sale || null,
        first_payment_date: formData.first_payment_date || null,
        first_payment_to_me: formData.first_payment_to_me || null,
        balloon_due_date: formData.balloon_due_date || null,
        offer_date: formData.offer_date || null,
        buyer_last_payment_date: formData.buyer_last_payment_date || null,
        buyer_estoppel_signed_date: formData.buyer_estoppel_signed_date || null,
        buyer_interview_completed_date: formData.buyer_interview_completed_date || null,
        seller_email: formData.seller_email || null,
        seller_phone: formData.seller_phone || null,
        seller_address: formData.seller_address || null,
        buyer_address: formData.buyer_address || null,
        buyer_payment_history: formData.buyer_payment_history || null,
        property_type: formData.property_type || null,
        property_description: formData.property_description || null,
        property_address: formData.property_address || null,
        bank_notes: formData.bank_notes || null,
        internal_notes: formData.internal_notes || null,
        zillow_link: formData.zillow_link || null
      };

      if (deal?.id) {
        const { error } = await supabase
          .from('deals')
          .update(dealData)
          .eq('id', deal.id);

        if (error) throw error;
        toast({ title: "Deal updated successfully" });
      } else {
        const { error } = await supabase
          .from('deals')
          .insert([dealData]);

        if (error) throw error;
        toast({ title: "Deal created successfully" });
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving deal:', error);
      toast({
        title: "Error saving deal",
        description: "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">
            {deal ? 'Edit Deal' : 'New Deal'}
          </h1>
        </div>
        <Button onClick={handleSubmit} disabled={isLoading} size="sm">
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Deal'}
        </Button>
      </div>

      {!deal && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <ContractUpload onDataExtracted={handleExtractedData} />
              <div className="grid grid-cols-2 gap-4">
                <CsvFileUpload onDataLoaded={handleExtractedData} />
                <JsonFileUpload onDataLoaded={handleExtractedData} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="parties">Parties</TabsTrigger>
                <TabsTrigger value="financial">Financial</TabsTrigger>
                <TabsTrigger value="property">Property</TabsTrigger>
                <TabsTrigger value="buyer-interview">Interview</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="mt-4">
                <BasicInfoTab formData={formData} onChange={handleInputChange} />
              </TabsContent>
              
              <TabsContent value="parties" className="mt-4">
                <PartyInfoTab formData={formData} onChange={handleInputChange} />
              </TabsContent>
              
              <TabsContent value="financial" className="mt-4">
                <FinancialTab formData={formData} onChange={handleInputChange} />
              </TabsContent>
              
              <TabsContent value="property" className="mt-4">
                <PropertyTab formData={formData} onChange={handleInputChange} />
              </TabsContent>
              
              <TabsContent value="buyer-interview" className="mt-4">
                <BuyerInterviewTab formData={formData} onChange={handleInputChange} />
              </TabsContent>
              
              <TabsContent value="notes" className="mt-4">
                <NotesTab formData={formData} onChange={handleInputChange} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
